"use client";

import { Button } from "@cmp-src/primitives/button";
import { VisitorTypeField } from "@gettactic/api/src/endpoints/visitor-manager";
import { FormWrapper } from "@gettactic/components/src/form/FormWrapper";
import { Modal } from "@gettactic/components/src/overlay/modals/Modal";
import { getApiErrorMessage } from "@gettactic/helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import React, { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { api } from "@/lib/api/api";
import {
  buildVisitCreatePayload,
  PRE_REGISTER_DEFAULT_VALUES,
  type PreRegisterFormValues,
  preRegisterSchema,
  VisitorPreRegistrationFormFields
} from "./VisitorPreRegistrationFormFields";

interface RegistrationStepFormProps {
  email: string;
  officeId: string;
  date: Date;
  time: Date;
  stepLabel: string;
  isLastGuest: boolean;
  onSuccess: (email: string, code: string | undefined) => void;
  onSkip: () => void;
  onCancel: () => void;
}

export const RegistrationStepForm: React.FC<RegistrationStepFormProps> = ({
  email,
  officeId,
  date,
  time,
  stepLabel,
  isLastGuest,
  onSuccess,
  onSkip,
  onCancel
}) => {
  const queryClient = useQueryClient();
  const customFieldsRef = useRef<VisitorTypeField[]>([]);
  const prevEmailRef = useRef(email);

  const defaultValues: PreRegisterFormValues = {
    ...PRE_REGISTER_DEFAULT_VALUES,
    office_id: officeId,
    visitor_email: email,
    expected_date: date,
    expected_time: format(time, "hh:mm a")
  };

  const form = useForm<PreRegisterFormValues>({
    resolver: zodResolver(preRegisterSchema),
    defaultValues
  });

  // Subscribe to formState.errors so child inputs re-render on validation failure
  const { errors: _formErrors } = form.formState;

  // When email prop changes (advancing to next guest), reset only guest-specific
  // fields while preserving visitor_type_id, host_id, office, date, and time
  useEffect(() => {
    if (prevEmailRef.current === email) return;
    prevEmailRef.current = email;

    form.setValue("visitor_email", email);
    form.setValue("visitor_name", "");

    // Clear custom field values
    for (const field of customFieldsRef.current) {
      form.setValue(field.id, "");
    }

    // Clear validation errors from the previous guest
    form.clearErrors();
  }, [email, form]);

  const createMutation = useMutation({
    mutationFn: async (data: PreRegisterFormValues) => {
      const payload = buildVisitCreatePayload(data, customFieldsRef.current);
      const response = await api.client.visits.create(payload);
      return response.result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["visits.list"]
      });
      onSuccess(email, result?.code ?? undefined);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Failed to pre-register visitor"));
    }
  });

  const handleSubmit = useCallback(
    () =>
      form.handleSubmit(async (data) => {
        await createMutation.mutateAsync(data);
      })(),
    [form, createMutation]
  );

  return (
    <Modal
      isOpen
      onClose={onCancel}
      title={`Pre-Register Visitor (${stepLabel})`}
      size="md"
    >
      <FormWrapper onSubmit={handleSubmit} isLoading={createMutation.isPending}>
        <VisitorPreRegistrationFormFields
          form={form}
          isActive
          readOnlyEmail
          customFieldsRef={customFieldsRef}
        />
      </FormWrapper>
      <Modal.Footer>
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="outline" onClick={onSkip}>
          Skip
        </Button>
        <Button
          onClick={handleSubmit}
          isLoading={createMutation.isPending}
          disabled={createMutation.isPending}
        >
          {isLastGuest ? "Register" : "Register & Next"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
